import { v4 as uuid } from 'uuid';

import {
  getFormData,
  formatAttachment,
  getShrineId,
} from 'hb-react/shared/hooks/useStorage/utils';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import { addSuffixToFieldName } from 'hb-react/shared/utils/fieldNames';

import {
  MAGIC_KEY,
  StorageStatus,
  VirusScanStatus,
  MALICIOUS_FILE,
  UNKNOWN_VIRUS_SCAN_STATUS_ERROR,
  ATTRIBUTES_SUFFIX,
  VIRUS_SCAN_TIMEOUT,
  VIRUS_SCAN_TIMEOUT_ERROR,
} from '../constants';
import { presign, upload, postUploadFO, postUploadBO } from '../requests';
import {
  type StorageRecordTypes,
  type StorageFileRecordFromPolyAttachment,
  type GlobalStorageFile,
  type StorageFileRecord,
  type GetInitialFiles,
  type GetStorageAttributesFieldName,
  type FindImagesInContent,
  type GetMagicKeyUrl,
  type GetAblyTokenType,
  type GetAblyVirusScanChannel,
  type FileMatchesIdentifiers,
  type IsInLoadingState,
  type AreFilesReadyToSubmit,
  type FormatAddedFileForSubmit,
  type FormatAddedFilesForSubmit,
  type FormatRemovedFilesForSubmit,
  type PresignAndUploadProps,
  type GetAblyVirusScanMessageHandler,
  type AttachFile,
  type StorageAttachmentAttributes,
} from '../types';
import { OwnerType } from '../types';

export const getStorageAttributesFieldName: GetStorageAttributesFieldName = (
  fieldName,
) => addSuffixToFieldName(fieldName, ATTRIBUTES_SUFFIX);

export const getGSFileIdentifiersFromContent = (contentHtml: string) => {
  if (!contentHtml) {
    return { recordIds: [], shrineKeys: [] };
  }

  return Array.from(contentHtml.matchAll(/<img.*?\/?>/g)).reduce(
    (result, [img]) => {
      const src = img?.match(/src=(["'])(.*?)\1/)?.[2];
      const recordId = src?.match(/\/storage\/(\d+)\/download/)?.[1];
      const shrineKey = src?.match(/shrine_cache\/.+$/)?.[0];

      if (recordId) {
        result.recordIds.push(recordId);
      }

      if (shrineKey) {
        result.shrineKeys.push(shrineKey);
      }

      return result;
    },
    { recordIds: [], shrineKeys: [] } as {
      recordIds: Array<string>;
      shrineKeys: Array<string>;
    },
  );
};

/** Search HTML content for img with a Global Storage src value
 * and return an array of GlobalStorageFile. It does NOT match newly
 * added images that have been attached to a record yet.
 * @param contentHtml
 * @returns GlobalStorageFile[]
 */
export const findImagesInContent: FindImagesInContent = (contentHtml) => {
  if (!contentHtml) {
    return [];
  }

  return Array.from(contentHtml.matchAll(/<img.*?\/?>/g)).reduce(
    (result, [img]) => {
      const src = img?.match(/src=(["'])(.*?)\1/)?.[2];
      const recordId = src?.match(/\/storage\/(\d+)\/download/)?.[1];
      const key = img?.match(/id=(["'])(.*?)\1/)?.[2];

      // Probably an external image? Just ignore
      if (!recordId) {
        return result;
      }

      return [
        ...result,
        {
          id: parseInt(recordId, 10),
          key: key ?? uuid(),
          download_url: `/storage/${recordId}/download`,
          storageStatus: StorageStatus.ATTACHED,
          abortController: new AbortController(),
        },
      ];
    },
    [] as Array<GlobalStorageFile>,
  );
};

function isStorageFileRecordFromPolyAttachment(
  o: StorageRecordTypes,
): o is StorageFileRecordFromPolyAttachment {
  if ((o as StorageFileRecordFromPolyAttachment).metadata) {
    return true;
  }

  return false;
}

function isStorageFileRecord(o: StorageRecordTypes): o is StorageFileRecord {
  if ((o as StorageFileRecord).file_url) {
    return true;
  }

  return false;
}

export const getFileRecordMetadata = (file: StorageRecordTypes) => {
  let id;
  let fileMetadata;
  let virusScanStatus;

  if (isStorageFileRecordFromPolyAttachment(file)) {
    id = file.id;
    fileMetadata = {
      name: file.metadata.filename,
      url: file.download_url,
      type: file.metadata.mime_type,
    };
    virusScanStatus = file.metadata.scanned;
  } else if (isStorageFileRecord(file)) {
    id = file.id;
    fileMetadata = {
      name: file.name,
      url: file.file_url,
      type: file.mime_type,
    };
    virusScanStatus = file.virus_scan_status;
  } else {
    id = file.id;
    fileMetadata = {
      name: file.filename,
      url: file.previewUrl,
      type: file.mimeType,
    };
    virusScanStatus = file.virusScanStatus;
  }

  return { id, fileMetadata, virusScanStatus };
};

export const getInitialFiles: GetInitialFiles = ({ files, contentHtml }) => {
  if (!files?.length) {
    return [];
  }

  const contentImages = findImagesInContent(contentHtml);

  const gsFiles = files.map((file): GlobalStorageFile => {
    const { id, fileMetadata, virusScanStatus } = getFileRecordMetadata(file);

    const gsFile = {
      fileMetadata,
      virusScanStatus,
      key: uuid(),
      id,
      storageStatus: StorageStatus.ATTACHED,
      download_url: `/storage/${id}/download`,
      error:
        virusScanStatus === VirusScanStatus.INFECTED ? MALICIOUS_FILE : null,
      abortController: new AbortController(),
    };

    // If we are not in inline images mode, return the upload as is
    if (!contentHtml) {
      return gsFile;
    }

    const contentImage = contentImages.find((image) => image.id === file.id);

    // Image is in the content
    if (contentImage) {
      // WARNING: order is important => gsFile before contentImage to
      //          make sure we are using the id extracted from the HTML
      //          as the key
      return {
        ...gsFile,
        ...contentImage,
      };
    }

    // Image is not in the content, set isRemoved = true
    return {
      ...gsFile,
      isRemoved: true,
    };
  });

  return gsFiles;
};

// Not really an URL but we put it in the `src` attribute of an <img> tag
export const getMagicKeyUrl: GetMagicKeyUrl = ({ shrineId, fieldName }) =>
  `${MAGIC_KEY}:${fieldName}:${shrineId}:${MAGIC_KEY}`;

export const getAblyVirusScanChannel: GetAblyVirusScanChannel = (
  ownerType,
  ownerId,
) => `base:uploads:${ownerType}:${ownerId}:virus-scan`;

export const getAblyTokenType: GetAblyTokenType = (ownerType) => {
  switch (ownerType) {
    case OwnerType.ADMIN:
      return 'backoffice';
    case OwnerType.USER:
      return 'frontoffice';
    default:
      throw new Error(`Unknown ownerType: ${ownerType}`);
  }
};

export const fileMatchesIdentifiers: FileMatchesIdentifiers = (
  file,
  identifiers,
) => {
  const { key, shrineKey, id } = identifiers;

  if (!key && !shrineKey && !id) {
    throw new Error('No key, shrineKey or id provided');
  }

  const matches = [] as Array<boolean>;

  if (key) {
    matches.push(file.key === key);
  }

  if (shrineKey) {
    matches.push(file.shrineKey === shrineKey);
  }

  if (id) {
    matches.push(file.id === id);
  }

  const perfectMatch = matches.every(Boolean);
  const partialMatch = !perfectMatch && matches.some(Boolean);

  if (partialMatch) {
    throw new Error('Partial matches should not happen');
  }

  return perfectMatch;
};

export const isInLoadingState: IsInLoadingState = (
  file,
  virusScanningEnabled,
) =>
  Boolean(
    !file.error &&
      (!file.storageStatus ||
        (file.uploadPercentage !== undefined && file.uploadPercentage < 100) ||
        // HACK: While we support QuilEditor V1 and V2, ignore the virus scan status
        // if attachment is not a GlobalStorageFile (i.e. if doesn't have a key defined)
        (virusScanningEnabled &&
          file.shrineKey &&
          (!file.virusScanStatus ||
            file.virusScanStatus === VirusScanStatus.PENDING))),
  );

export const areFilesReadyToSubmit: AreFilesReadyToSubmit = (
  files,
  virusScanningEnabled,
) =>
  files
    .filter((file) => file && !file.isRemoved)
    .every((file) => {
      if (file.error) {
        return false;
      }

      // Existing file
      if (
        file.storageStatus === StorageStatus.ATTACHED &&
        file.id &&
        file.virusScanStatus !== VirusScanStatus.INFECTED
      ) {
        return true;
      }

      // Even if virus scanning is disabled, this should still have a value by the time
      // the file is ready to submit.
      if (!file.virusScanStatus) {
        return false;
      }

      if (virusScanningEnabled) {
        // Only CLEAN files accepted
        // TEMP: PS-6392 - Files uploaded while virus-scanning is disabled are in the NOT_SCNANABLE status.
        //                 Accept NOT_SCANNABLE files for now until we have a process in place to scan them
        //                 automatically when virus-scanning is re-enabled.
        if (
          ![VirusScanStatus.NOT_SCANNABLE, VirusScanStatus.CLEAN].includes(
            file.virusScanStatus,
          )
        ) {
          return false;
        }
      } else if (
        // Existing files could have been previously scanned so accept CLEAN and NOT_SCANNABLE
        ![VirusScanStatus.NOT_SCANNABLE, VirusScanStatus.CLEAN].includes(
          file.virusScanStatus,
        )
      ) {
        return false;
      }

      // New file need a shrineKey
      if (file.storageStatus === StorageStatus.UPLOADED && !file.shrineKey) {
        return false;
      }

      // Existing files need an id
      if (file.storageStatus === StorageStatus.ATTACHED && !file.id) {
        return false;
      }

      return true;
    });

export const formatAddedFileForSubmit: FormatAddedFileForSubmit = (gsFile) => {
  if (!gsFile) {
    return gsFile;
  }

  const { id, isRemoved, file, shrineKey } = gsFile;

  if (!isRemoved && !id && file && shrineKey) {
    return formatAttachment({ file, id: getShrineId(shrineKey) });
  }

  return null;
};

export const formatAddedFilesForSubmit: FormatAddedFilesForSubmit = (files) =>
  files
    .filter(
      ({ id, isRemoved, file, shrineKey }) =>
        !isRemoved && !id && file && shrineKey,
    )
    .map(({ file, shrineKey }) =>
      // @ts-expect-error file and shrineKey validated in filter above
      formatAttachment({ file, id: getShrineId(shrineKey) }),
    );

export const formatRemovedFilesForSubmit: FormatRemovedFilesForSubmit = (
  files,
) =>
  files
    .filter(
      ({ id, isRemoved }) =>
        (typeof id === 'number' || typeof id === 'string') && isRemoved,
    )
    .map(
      ({ id }) =>
        ({
          id,
          _destroy: true,
        }) as StorageAttachmentAttributes,
    );

export const presignAndUpload = async ({
  controller,
  file,
  inBO,
  key,
  networkId,
  onProgress,
  updateStoredFile,
  virusScanningEnabled,
}: PresignAndUploadProps) => {
  // PRESIGN
  const {
    data: { url, fields, method },
  } = await presign(controller, file.name, file.type);

  const shrineKey = fields.key;

  updateStoredFile({ key }, { shrineKey });

  // UPLOAD
  const handleProgress = (percentage: number) => {
    onProgress?.(percentage);
    updateStoredFile({ key }, { uploadPercentage: percentage });
  };

  await upload({
    method,
    url,
    controller,
    formData: getFormData(fields, file),
    onUploadProgress: ({ loaded, total }) =>
      handleProgress(Math.ceil((loaded * 100) / total)),
  });

  updateStoredFile(
    { key },
    {
      uploadPercentage: 100,
      storageStatus: StorageStatus.UPLOADED,
    },
  );

  // TRIGGER VIRUS SCAN AND ABLY ARE ENABLED
  const virusScanStatus = virusScanningEnabled
    ? VirusScanStatus.PENDING
    : VirusScanStatus.NOT_SCANNABLE;

  const virusScanTimeout = virusScanningEnabled
    ? setTimeout(() => {
        updateStoredFile(
          { key },
          {
            error: VIRUS_SCAN_TIMEOUT_ERROR,
          },
        );
      }, VIRUS_SCAN_TIMEOUT)
    : undefined;

  updateStoredFile(
    { key },
    {
      virusScanStatus,
      virusScanTimeout,
    },
  );

  if (virusScanningEnabled) {
    if (inBO) {
      if (!networkId) {
        throw new Error('Network id is required for postUploadBO');
      }

      await postUploadBO(shrineKey, networkId);
    } else {
      await postUploadFO(shrineKey);
    }
  }

  return `${url}/${shrineKey}`;
};

export const getAblyVirusScanMessageHandler: GetAblyVirusScanMessageHandler =
  (updateStoredFile, markFileAsRemovedOnError = false) =>
  ({ data }) => {
    if (!data) {
      return;
    }

    const { key: shrineKey, scan_status: virusScanStatus } = data;

    const getUpdateFunc =
      (updatedData: Partial<GlobalStorageFile>) =>
      (prevFile: GlobalStorageFile) => {
        // Clear the timeout now that we've received a response
        prevFile.virusScanTimeout && clearTimeout(prevFile.virusScanTimeout);

        return {
          ...prevFile,
          ...updatedData,
        };
      };

    switch (virusScanStatus) {
      case VirusScanStatus.CLEAN:
        updateStoredFile(
          { shrineKey },
          getUpdateFunc({
            virusScanStatus,
          }),
        );
        break;
      case VirusScanStatus.INFECTED:
        updateStoredFile(
          { shrineKey },
          getUpdateFunc(
            markFileAsRemovedOnError
              ? {
                  virusScanStatus,
                  error: MALICIOUS_FILE,
                  isRemoved: true,
                }
              : {
                  virusScanStatus,
                  error: MALICIOUS_FILE,
                },
          ),
        );
        break;
      default:
        updateStoredFile(
          { shrineKey },
          getUpdateFunc({
            virusScanStatus,
            error: UNKNOWN_VIRUS_SCAN_STATUS_ERROR,
            isRemoved: markFileAsRemovedOnError,
          }),
        );
    }
  };

export const attachFile: AttachFile = async ({
  attachableType,
  attachableId,
  fieldName,
  file,
}) => {
  const { data } = await axios.post(
    Routes.storage_create_attachment_path({ format: 'json' }),
    {
      attachment: {
        attachable_type: attachableType,
        attachable_id: attachableId,
        name: fieldName,
        blob: file,
      },
    },
  );

  return {
    ...data,
  };
};
